/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Style, SEO and settings
import Helmet from 'react-helmet';
import { Col, Grid, Row, styled, withTheme, th } from '@smooth-ui/core-sc';

// Additional Components/Containers
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';
import Button from '_platform/src/components/Button/Button';

import { withSettings } from 'containers/WebApp/SettingsContext';
import LoadAsync from '_platform/src/utils/LoadAsync';
import logo from './images/logo.png';
import bgImage1x from './images/bgImage@1x.jpg';
import bgImage2x from './images/bgImage@2x.jpg';

const LoginProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginProvider" */ '_platform/src/containers/LoginProvider/LoginProvider'
  )
);
const LoginForm = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginForm" */ '_platform/src/components/LoginForm/LoginForm'
  )
);

const PageContainer = styled.div`
  align-items: center;
  background-color: #fff;
  background-image: url(${bgImage1x});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  @media only screen and (min-device-pixel-ratio: 1.5),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) {
    background-color: #fff;
    background-image: url(${bgImage2x});
  }
`;

const LoginLogo = styled.div`
  margin-bottom: 0;
  margin-top: 0;
  padding-bottom: 0;
  padding-top: 0;
  img {
    height: auto;
    width: 350px;
  }
`;

const IntroContainer = styled.div`
  box-shadow: 0px 10px 20px #00000046;
  background-color: white;
  box-sizing: border-box;
  margin: 0 auto;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  padding: 1.7rem;
  max-width: 357px;
  border-radius: 20px;
  color: ${th('primary')};
  h5 {
    font-size: 17px;
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    padding-bottom: 0;
    padding-top: 0;
  }

  input {
    font-size: 14px;
  }

  a {
    font-size: 12px;
    color: ${th('primary')};
    &:visited {
      color: ${th('primary')};
    }
  }

  .sui-box {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .reset-password {
    margin-top: 0;
  }

  label {
    color: ${th('primary')};
    font-size: 12px;
  }

  button {
    background-color: #fff;
    border: 1px solid ${th('primary')};
    border-radius: 4px;
    font-weight: bold;
    color: ${th('primary')};
    text-transform: uppercase;
    width: 100%;
    margin: 0;
    &:hover {
      color: #fff;
    }
  }

  .register-link {
    display: block;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    text-decoration: none;
    text-transform: inherit;
    &:visited {
      color: inherit;
    }
    &:hover {
      color: inherit;
    }
  }
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>

    <PageContainer className="page-container">
      <Grid>
        <Row justifyContent="center">
          <Col>
            <IntroContainer>
              <LoginLogo>
                <img
                  src={logo}
                  alt={
                    settings.settingsApp && settings.settingsApp.siteName
                      ? settings.settingsApp.siteName
                      : 'ProgramName'
                  }
                />
              </LoginLogo>

              <h5 className="text--center">
                Welcome to
                <br />
                Double A Ream Rewards
              </h5>
              <LoginProvider disableMeta>
                <LoginForm formLabels={{ labelUserName: 'Email' }} />
              </LoginProvider>
              <div className="spacer--top--small">
                <Button>
                  <Link to="/register/" className="register-link">
                    Register here
                  </Link>
                </Button>
              </div>
            </IntroContainer>
          </Col>
        </Row>
      </Grid>
    </PageContainer>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
